<template>
	<div class="table-container">
		<div class="table-scrollable">
			<table class="table">
				<thead>
					<tr>
						<!-- Checkbox column -->
						<typography
							tag="th"
							weight="600"
							color="gray600"
							vertical-align="center"
							align="center"
							class="checkbox-column"
						>
							<uncheckbox v-model="allSelected" @click="toggleAll"></uncheckbox>
						</typography>
						<!-- Dynamic columns or single header -->

						<typography
							v-for="column in columns"
							:key="column.field"
							tag="th"
							weight="600"
							color="gray600"
							vertical-align="center"
							align="center"
							:class="{ 'highlighted-column': column.sortKey === sortKey }"
						>
							<div
								class="d-flex align-center justify-content-center"
								style="height: 100%"
								@click="changeSort(column.sortKey)"
							>
								{{ column.title }}
							</div>
						</typography>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, index) in sortedData" :key="index">
						<!-- Checkbox cell -->
						<td class="checkbox-column">
							<uncheckbox
								v-model="item.selected"
								@update:modelValue="emitSelected"
							></uncheckbox>
						</td>
						<!-- Dynamic columns or single data cell -->
						<typography
							v-for="column in columns"
							:key="column.field"
							color="gray500"
							align="center"
							tag="td"
							:class="{ strikethrough: item.selected, 'clickable-entity': isClickableEntity(column.field) }"
							@click="handleEntityClick(item, column.field)"
						>
							{{ item[column.field] }}
						</typography>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script setup lang="ts">
import Typography from "@/components/typography.vue";
import uncheckbox from "@/components/global/uncheckbox.vue";
import { computed, ref, watch } from "vue";

const props = defineProps({
	data: {
		type: Array as () => Array<{ [key: string]: any; selected: boolean }>,
		required: true,
	},

	columns: {
		type: Array as () => Array<{ title: string; field: string; sortKey?: string }>,
		default: () => [],
	},
});

const emit = defineEmits<{
  (e: 'selection-change', ...args: any[]): void;
  (e: 'sort', ...args: any[]): void;
  (e: 'entity-click', entity: any, field: string): void;
}>();

const allSelected = ref(false);
const sortKey = ref("");

function emitSelected() {
	const selectedItems = props.data.filter((item) => item.selected === true);
	console.log("Selected items:", selectedItems);
	emit("selection-change", selectedItems);
}
const toggleAll = () => {
	allSelected.value = !allSelected.value;
	props.data.forEach((item) => {
		item.selected = allSelected.value;
	});
	emitSelected();
};

const sortedData = computed(() => {
	return [...props.data].sort((a, b) => {
		const field =
			props.columns.find((col) => col.sortKey === sortKey.value)?.field || sortKey.value;
		if (typeof a[field] === "string" && typeof b[field] === "string") {
			return a[field].localeCompare(b[field]);
		}
		return 0;
	});
});

const changeSort = (key: string) => {
	sortKey.value = key;
	emit("sort", key);
};

// Check if a field represents an entity name that should be clickable
const isClickableEntity = (field: string) => {
	const clickableFields = [
		'child_fname', 'child_lname', // Student names
		'teacher_fname', 'teacher_lname', // Teacher names
		'firstname', 'lastname', // Parent names
		'team_name', // Team names
		'title' // Class names
	];
	return clickableFields.includes(field);
};

// Handle entity name clicks
const handleEntityClick = (entity: any, field: string) => {
	if (isClickableEntity(field)) {
		emit('entity-click', entity, field);
	}
};

watch(
	() => props.data,
	(newData) => {
		allSelected.value = newData.length > 0 && newData.every((item) => item.selected);
	},
	{ immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.table-container {
	margin-top: 30px;
	border: 1px solid #d7d7d7;
	border-radius: 5px;
	overflow: hidden;

	.table-scrollable {
		max-height: 50vh;
		&:has(.table tbody:not(:empty)) {
			overflow: auto;
		}
	}

	.table {
		border-collapse: collapse;
		border-spacing: 0;
		width: 100%;
		border: 0px;
		margin: 0;

		thead,
		th {
			position: sticky;
			top: 0;
			left: 0;
			z-index: 2;
			background-color: #f3f6f8;
			user-select: none;
		}

		th,
		td {
			border-bottom: 1px solid #d7d7d7;
			&:not(:last-child) {
				border-right: 1px solid #d7d7d7;
			}
			padding: 0.5rem;
			height: 0;
			> span,
			> div {
				height: 100%;
			}
		}

		tr {
			&:last-child td {
				border-bottom: 0px;
			}
		}

		.checkbox-column {
			width: 10px;
			text-align: center;
			vertical-align: middle;
		}

		.strikethrough {
			text-decoration: line-through;
			color: #aaa;
		}

		.highlighted-column {
			background-color: $secondary-100;
		}

		.clickable-entity {
			cursor: pointer;
			color: $primary-600 !important;
			text-decoration: underline;
			
			&:hover {
				color: $primary-700 !important;
				background-color: rgba($primary-600, 0.1);
			}
		}
	}
}
</style>
